import type { ThemeTokens, BrandColors } from '@/shared/ui/theme/tokens';

interface DeleteMethodConfirmModalProps {
  method: { kind: 'facial' | 'nfc'; label: string };
  employeeName: string;
  onCancel: () => void;
  onConfirm: () => void;
  isPending: boolean;
  cancelLabel?: string;
  confirmLabel?: string;
  pendingLabel?: string;
  T: ThemeTokens;
  C: BrandColors;
}

/**
 * DeleteMethodConfirmModal — confirmation dialog before deleting a biometric method.
 *
 * Visual fidelity: matches `web/Clock Web.html` confirmDelete modal block exactly.
 * - Overlay: fixed, inset 0, T.modalBg backdrop, zIndex 1000
 * - Modal: maxWidth 380, borderRadius 16, centered, column flex
 * - Icon: 54×54, borderRadius 14, ${C.danger}18 background
 * - Title: fontSize 17, fontWeight 700
 * - Body: fontSize 12, T.textMuted
 * - Buttons: flex gap 10, cancel (transparent border) + confirm (C.danger)
 * - isPending: disables BOTH buttons (race condition guard, design R4)
 *
 * Change: nueva-seccion-de-borrado-perfiles-en-administracion-empleados
 */
export function DeleteMethodConfirmModal({
  method,
  employeeName,
  onCancel,
  onConfirm,
  isPending,
  cancelLabel = 'Cancelar',
  confirmLabel = 'Eliminar',
  pendingLabel = 'Eliminando…',
  T,
  C,
}: DeleteMethodConfirmModalProps) {
  return (
    <div
      onClick={!isPending ? onCancel : undefined}
      style={{
        position: 'fixed',
        inset: 0,
        background: T.modalBg,
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: T.surfaceBg,
          borderRadius: 16,
          width: '100%',
          maxWidth: 380,
          boxShadow: T.shadow,
          padding: '26px 26px 22px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
        }}
      >
        {/* Trash icon container */}
        <div
          data-testid="delete-modal-icon"
          style={{
            width: 54,
            height: 54,
            borderRadius: 14,
            background: `${C.danger}18`,
            color: C.danger,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 14,
          }}
        >
          <svg width={26} height={26} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="3 6 5 6 21 6" />
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
          </svg>
        </div>

        {/* Title */}
        <h2
          data-testid="delete-modal-title"
          style={{ fontSize: 17, fontWeight: 700, color: T.clockText, marginBottom: 7 }}
        >
          {method.label}
        </h2>

        {/* Body */}
        <p
          data-testid="delete-modal-body"
          style={{ fontSize: 12, color: T.textMuted, lineHeight: 1.6, marginBottom: 20 }}
        >
          ¿Seguro que querés eliminar el método{' '}
          <b style={{ color: T.text }}>{method.label}</b> enrolado de{' '}
          <b style={{ color: T.text }}>{employeeName}</b>?{' '}
          Esta acción no se puede deshacer.
        </p>

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: 10, width: '100%' }}>
          <button
            data-testid="delete-modal-cancel"
            onClick={onCancel}
            disabled={isPending}
            style={{
              flex: 1,
              padding: '11px',
              borderRadius: 9,
              border: `1.5px solid ${T.border}`,
              background: 'transparent',
              color: T.text,
              fontFamily: 'inherit',
              fontSize: 12,
              fontWeight: 600,
              cursor: isPending ? 'not-allowed' : 'pointer',
              opacity: isPending ? 0.6 : 1,
            }}
          >
            {cancelLabel}
          </button>
          <button
            data-testid="delete-modal-confirm"
            onClick={onConfirm}
            disabled={isPending}
            style={{
              flex: 1,
              padding: '11px',
              borderRadius: 9,
              border: 'none',
              background: isPending ? `${C.danger}88` : C.danger,
              color: '#fff',
              fontFamily: 'inherit',
              fontSize: 12,
              fontWeight: 700,
              cursor: isPending ? 'not-allowed' : 'pointer',
              boxShadow: `0 4px 12px ${C.danger}33`,
            }}
          >
            {isPending ? pendingLabel : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
